/**
 * WorklistItem — one row of the app's own punch list.
 *
 * UX rework 2026-02: editing (title/description/type/priority/blocking/
 * home/also-affects/screenshot) is inline for the item's author or any
 * super-admin. The old auto-advance "prompted >" chip is replaced with
 * an explicit status menu that shows the current status plainly, lists
 * every move (forward AND backward) as a verb, and requires a confirm
 * click. Verified stays super-admin-only. Every content change appends
 * to `edit_history[]`; every status change appends to `status_history[]`.
 * Both are expandable inline on the row.
 *
 * Environment rule: worklist statuses are per-environment data. Preview
 * status changes never sync to prod — prod is set only by the owner in
 * prod. See the Rulebook feature entry for the canonical statement.
 *
 * DESIGN PASS 4: converted onto the token system with the Rule Book page.
 * Styles live in styles/pages/rulebook.css, scoped to .km-rulebook.
 * This file is OFF the design-token allowlist.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertOctagon, ChevronDown,
  Image as ImageIcon, Pencil, X, Check, History, Upload,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_STATUSES = ['open', 'prompted', 'built', 'verified'];

// Display labels only — the stored values are unchanged.
export const STATUS_LABEL = {
  open: 'Open',
  prompted: 'Prompted',
  built: 'Built — not yet confirmed',
  verified: 'Completed',
  withdrawn: 'Not doing',
};
export const REMAINING_STATUSES = ['open', 'prompted', 'built'];
export const COMPLETED_STATUSES = ['verified', 'withdrawn'];

const VERB = {
  open: 'Move to Open',
  prompted: 'Move to Prompted',
  built: 'Move to Built — not yet confirmed',
  verified: 'Mark Completed',
  withdrawn: 'Mark Not doing',
};

export default function WorklistItem({
  item, featureById, isSuperAdmin, onChanged, showHomeChip = false,
  currentUserId,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingStatus, setConfirmingStatus] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);

  // Below md the status menu is a bottom sheet, so it needs a way out that
  // isn't the toggle button hidden behind it.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => { setMenuOpen(false); setConfirmingStatus(null); };
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const canEdit = isSuperAdmin || item.author_user_id === currentUserId;

  const patch = async (payload) => {
    try {
      const { data } = await axios.patch(`${API}/rulebook/worklist/${item.id}`, payload);
      onChanged?.(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
      throw e;
    }
  };

  const commitStatus = async (next) => {
    if (next === 'verified' && !isSuperAdmin) {
      toast.error('Only the owner (super-admin) can mark items Verified.');
      return;
    }
    try {
      await patch({ status: next });
      toast.success(`Moved to ${STATUS_LABEL[next] || next}.`);
    } catch { /* toast already shown */ }
    setConfirmingStatus(null);
    setMenuOpen(false);
  };

  const home = item.feature_id ? featureById(item.feature_id) : null;
  const isBlocking = !!(item.blocking && item.status === 'open');

  return (
    <div className="km-wl" data-blocking={isBlocking} data-testid={`worklist-item-${item.id}`}>
      {/* Header row: badges + edit + status control */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="km-status"
              data-status={item.status}
              data-testid={`worklist-item-status-${item.id}`}
            >
              {STATUS_LABEL[item.status] || item.status}
            </span>
            <span className="km-tag">{item.type}</span>
            {item.priority && <span className="km-tag">{item.priority}</span>}
            {item.blocking && (
              <span className="km-status km-status--blocking">
                <AlertOctagon className="w-3 h-3 mr-1 inline" />BLOCKING
              </span>
            )}
            {showHomeChip && home && <span className="km-meta">home: {home.name}</span>}
            {showHomeChip && !home && (
              <span className="km-meta">home: (unassigned{item.label ? ` · ${item.label}` : ''})</span>
            )}
            {item.edit_history?.length > 0 && (
              <span className="km-meta" title={`edited ${item.edit_history.length} time(s)`}>
                · edited {item.edit_history.length}×
              </span>
            )}
          </div>

          {!editing && (
            <>
              <div className="km-wl-title">{item.title}</div>
              {item.description && (
                <div className="km-wl-desc">{item.description}</div>
              )}
              {item.screenshot_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${item.screenshot_url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="km-link mt-2"
                  data-testid={`worklist-item-screenshot-${item.id}`}
                >
                  <ImageIcon className="w-3 h-3" /> screenshot
                </a>
              )}
              {(item.also_affects || []).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {item.also_affects.map(aa => {
                    const af = featureById(aa.feature_id);
                    if (!af) return null;
                    return (
                      <span
                        key={aa.feature_id}
                        className="km-status km-status--affects"
                        title={aa.note || ''}
                      >
                        affects: {af.name}{aa.note ? ` — ${aa.note}` : ''}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="km-meta mt-1">
                by {item.author_name} · {new Date(item.created_at).toLocaleDateString()}
                {item.edited_at && <> · last edit {new Date(item.edited_at).toLocaleDateString()}</>}
              </div>
            </>
          )}

          {editing && (
            <EditForm
              item={item} featureById={featureById}
              saving={saving} setSaving={setSaving}
              onDone={(updated) => { setEditing(false); if (updated) onChanged?.(updated); }}
              patch={patch}
            />
          )}
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          {/* Explicit status menu */}
          <div className="relative" ref={menuRef}>
            <button
              className="km-btn"
              onClick={() => { setMenuOpen(v => !v); setConfirmingStatus(null); }}
              data-testid={`worklist-item-status-menu-${item.id}`}
            >
              Status: <span className="km-prose--strong">{STATUS_LABEL[item.status] || item.status}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {menuOpen && (
              <div className="km-menu" data-testid={`worklist-status-dropdown-${item.id}`}>
                <div className="km-menu-head">
                  Current: <span className="km-menu-head-value">{STATUS_LABEL[item.status] || item.status}</span>
                </div>
                {ALL_STATUSES.filter(s => s !== item.status).map(s => (
                  <StatusOption
                    key={s} target={s}
                    label={VERB[s]}
                    disabled={s === 'verified' && !isSuperAdmin}
                    confirming={confirmingStatus === s}
                    onArm={() => setConfirmingStatus(s)}
                    onConfirm={() => commitStatus(s)}
                    onCancel={() => setConfirmingStatus(null)}
                    itemId={item.id}
                  />
                ))}
                {item.status !== 'withdrawn' && (
                  <StatusOption
                    target="withdrawn"
                    label={VERB.withdrawn}
                    confirming={confirmingStatus === 'withdrawn'}
                    onArm={() => setConfirmingStatus('withdrawn')}
                    onConfirm={() => commitStatus('withdrawn')}
                    onCancel={() => setConfirmingStatus(null)}
                    itemId={item.id}
                  />
                )}
              </div>
            )}
          </div>

          {canEdit && !editing && (
            <button
              className="km-btn km-btn--quiet"
              onClick={() => setEditing(true)}
              data-testid={`worklist-item-edit-${item.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          <button
            className="km-btn km-btn--quiet"
            onClick={() => setShowHistory(v => !v)}
            data-testid={`worklist-item-history-toggle-${item.id}`}
          >
            <History className="w-3 h-3" /> {showHistory ? 'Hide' : 'History'}
          </button>
        </div>
      </div>

      {/* Expandable status + edit history */}
      {showHistory && (
        <div className="km-history space-y-2" data-testid={`worklist-item-history-${item.id}`}>
          <div>
            <div className="km-history-head">Status history</div>
            <ul className="km-history-list space-y-0.5">
              {(item.status_history || []).map((h, i) => (
                <li key={i}>
                  <code className="km-code">{STATUS_LABEL[h.status] || h.status}</code>
                  <span className="km-history-when"> · {new Date(h.at).toLocaleString()} · {h.by_user_name}</span>
                </li>
              ))}
              {(item.status_history || []).length === 0 && <li className="km-history-when">(none)</li>}
            </ul>
          </div>
          {(item.edit_history || []).length > 0 && (
            <div>
              <div className="km-history-head">Edit history</div>
              <ul className="km-history-list space-y-0.5">
                {item.edit_history.map((e, i) => (
                  <li key={i}>
                    <span className="km-history-when">{new Date(e.at).toLocaleString()} · {e.by_user_name} · </span>
                    {e.changes.map(c => c.field).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusOption({ target, label, disabled, confirming, onArm, onConfirm, onCancel, itemId }) {
  if (disabled) {
    return (
      <div className="km-menu-option km-menu-option--off" title="Super-admin only">
        {label} <span className="km-meta">— super-admin only</span>
      </div>
    );
  }
  if (!confirming) {
    return (
      <button
        onClick={onArm}
        className="km-menu-option"
        data-testid={`worklist-status-option-${target}-${itemId}`}
      >
        {label}
      </button>
    );
  }
  return (
    <div className="km-menu-confirm">
      <span>{label}?</span>
      <div className="flex gap-1">
        <button
          onClick={onCancel}
          className="km-icon-btn"
          data-testid={`worklist-status-cancel-${target}-${itemId}`}
          aria-label="Cancel"
        >
          <X className="w-3 h-3" />
        </button>
        <button
          onClick={onConfirm}
          className="km-icon-btn km-icon-btn--ok"
          data-testid={`worklist-status-confirm-${target}-${itemId}`}
          aria-label="Confirm"
        >
          <Check className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function EditForm({ item, featureById, saving, setSaving, onDone, patch }) {
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [type, setType] = useState(item.type || 'fix');
  const [priority, setPriority] = useState(item.priority || '');
  const [blocking, setBlocking] = useState(!!item.blocking);
  const [featureId, setFeatureId] = useState(item.feature_id || '');
  const [alsoAffects, setAlsoAffects] = useState(item.also_affects || []);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const fileInputRef = useRef(null);
  // Feature list from window (we don't want to re-import rulebookData here — keep this component light)
  const allFeatures = window.__rulebookFeatures || [];

  const toggle = (fid) => setAlsoAffects(prev => prev.find(x => x.feature_id === fid)
    ? prev.filter(x => x.feature_id !== fid)
    : [...prev, { feature_id: fid, note: '' }]);

  const save = async () => {
    if (!title.trim()) { toast.error('Title is required.'); return; }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        type, priority: priority.trim() || null,
        blocking,
        feature_id: featureId || null,
        also_affects: alsoAffects.filter(a => a.feature_id && a.feature_id !== featureId),
      };
      let updated = await patch(body);
      if (screenshotFile) {
        const fd = new FormData();
        fd.append('file', screenshotFile);
        try {
          const { data: sc } = await axios.post(
            `${API}/rulebook/worklist/${item.id}/screenshot`, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });
          updated = { ...updated, screenshot_url: sc.screenshot_url };
        } catch { toast.error('Screenshot upload failed — text changes saved.'); }
      }
      toast.success('Item updated.');
      onDone(updated);
    } catch { /* handled */ }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-1 space-y-2" data-testid={`worklist-item-edit-form-${item.id}`}>
      <input className="km-input" value={title} onChange={e => setTitle(e.target.value)} data-testid={`worklist-edit-title-${item.id}`} />
      <textarea className="km-textarea" value={description} onChange={e => setDescription(e.target.value)} rows={2} data-testid={`worklist-edit-description-${item.id}`} />
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1">
          <span className="km-meta">Type:</span>
          {['fix', 'addition'].map(t => (
            <button key={t} onClick={() => setType(t)} className="km-seg" data-on={type === t}>
              {t}
            </button>
          ))}
        </div>
        <input className="km-input km-input--inline" value={priority} onChange={e => setPriority(e.target.value)} placeholder="priority (P1, HYGIENE…)" />
        <label className="km-checkline" data-on={blocking}>
          <input type="checkbox" checked={blocking} onChange={e => setBlocking(e.target.checked)} data-testid={`worklist-edit-blocking-${item.id}`} />
          <span>Blocking</span>
        </label>
        <label className="km-checkline">
          <Upload className="w-3 h-3" />
          <input ref={fileInputRef} type="file" accept="image/*"
            onChange={e => setScreenshotFile(e.target.files?.[0] || null)}
            className="km-file" data-testid={`worklist-edit-screenshot-${item.id}`} />
          {screenshotFile && <span className="km-meta truncate max-w-[8rem]">{screenshotFile.name}</span>}
        </label>
      </div>
      <div>
        <div className="km-field-label">Home feature</div>
        <select value={featureId} onChange={e => setFeatureId(e.target.value)}
          className="km-select"
          data-testid={`worklist-edit-home-${item.id}`}>
          <option value="">— New / Unassigned —</option>
          {allFeatures.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>
      <details open>
        <summary className="km-summary">Also affects</summary>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
          {allFeatures.filter(f => f.id !== featureId).map(f => {
            const on = alsoAffects.find(x => x.feature_id === f.id);
            return (
              <button key={f.id} onClick={() => toggle(f.id)}
                className="km-affect" data-on={!!on}
                data-testid={`worklist-edit-affects-${f.id}-${item.id}`}>
                {f.name}
              </button>
            );
          })}
        </div>
      </details>
      <div className="flex justify-end gap-2">
        <button className="km-btn km-btn--quiet" onClick={() => onDone(null)} disabled={saving} data-testid={`worklist-edit-cancel-${item.id}`}>Cancel</button>
        <button className="km-btn km-btn--primary" onClick={save} disabled={saving} data-testid={`worklist-edit-save-${item.id}`}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
